import { useEffect, useMemo, useState, Fragment } from "react";
import FinanceLayout from "@/components/FinanceLayout";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, PlusCircle, Calendar as CalIcon } from "lucide-react";
import ShootModal from "./ShootModal";

const MONTHS_TR = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];
const DAYS_TR = ["Pzt","Sal","Çar","Per","Cum","Cmt","Paz"];
const HOURS = Array.from({ length: 13 }, (_, i) => i + 8); // 08:00 - 20:00

const STATUS_COLORS = {
  onay_bekleniyor: { bg: "bg-amber-100", border: "border-amber-300", text: "text-amber-900", dot: "bg-amber-400", label: "Onay Bekleniyor" },
  onaylandi: { bg: "bg-emerald-100", border: "border-emerald-300", text: "text-emerald-900", dot: "bg-emerald-500", label: "Onaylandı" },
  iptal: { bg: "bg-red-100", border: "border-red-300", text: "text-red-900", dot: "bg-red-500", label: "İptal", lineThrough: true },
};

const startOfWeek = (d) => {
  const day = d.getDay(); // 0=Sun ... 6=Sat
  const diff = (day + 6) % 7; // days since Monday
  const nd = new Date(d); nd.setDate(d.getDate() - diff); nd.setHours(0, 0, 0, 0);
  return nd;
};
const addDays = (d, n) => { const nd = new Date(d); nd.setDate(d.getDate() + n); return nd; };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function ShootSchedule() {
  const { workspace } = useWorkspace();
  const [view, setView] = useState("week"); // week | month | list
  const [refDate, setRefDate] = useState(() => new Date());
  const [shoots, setShoots] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [statusFilter, setStatusFilter] = useState(""); // "" | onay_bekleniyor | onaylandi | iptal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const [modalShoot, setModalShoot] = useState(null);

  const range = useMemo(() => {
    if (view === "month") {
      const first = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
      const last = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0);
      return { from: iso(first), to: iso(last) };
    }
    if (view === "list") {
      const first = new Date(refDate.getFullYear() - 1, 0, 1);
      const last = new Date(refDate.getFullYear() + 1, 11, 31);
      return { from: iso(first), to: iso(last) };
    }
    const s = startOfWeek(refDate);
    return { from: iso(s), to: iso(addDays(s, 6)) };
  }, [refDate, view]);

  const load = async () => {
    try {
      const [phs, sh] = await Promise.all([
        api.get("/photographers", { params: { workspace } }),
        api.get("/shoots", { params: { workspace, date_from: range.from, date_to: range.to } }),
      ]);
      setPhotographers(phs.data);
      setShoots(sh.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, "Yüklenemedi")); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace, range.from, range.to]);

  const filtered = statusFilter ? shoots.filter((s) => s.status === statusFilter) : shoots;

  const shootsByDate = useMemo(() => {
    const m = {};
    filtered.forEach((s) => { (m[s.date] ||= []).push(s); });
    Object.values(m).forEach((arr) => arr.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")));
    return m;
  }, [filtered]);

  const nav = (dir) => {
    const nd = new Date(refDate);
    if (view === "month") nd.setMonth(refDate.getMonth() + dir);
    else if (view === "week") nd.setDate(refDate.getDate() + dir * 7);
    else nd.setMonth(refDate.getMonth() + dir);
    setRefDate(nd);
  };

  const openNew = (d) => { setModalDate(d ? iso(d) : iso(new Date())); setModalShoot(null); setModalOpen(true); };
  const openEdit = (s) => { setModalShoot(s); setModalDate(s.date); setModalOpen(true); };
  const onSaved = () => { setModalOpen(false); setModalShoot(null); load(); };

  const weekStart = startOfWeek(refDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const headerLabel = useMemo(() => {
    if (view === "month") return `${MONTHS_TR[refDate.getMonth()]} ${refDate.getFullYear()}`;
    if (view === "list") return `${MONTHS_TR[refDate.getMonth()]} ${refDate.getFullYear()}`;
    const end = addDays(weekStart, 6);
    return `${weekStart.getDate()} ${MONTHS_TR[weekStart.getMonth()]} — ${end.getDate()} ${MONTHS_TR[end.getMonth()]} ${end.getFullYear()}`;
  }, [refDate, view, weekStart]);

  return (
    <FinanceLayout>
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <CalendarDays size={28} className="text-violet-700" /> Çekim Programı
          </h1>
          <p className="text-sm text-slate-500 mt-2">Haftalık/aylık takvim üzerinde çekimlerinizi planlayın. Durum değişikliğinde çekimciye otomatik e-posta gider.</p>
        </div>
        <button onClick={() => openNew()} data-testid="shoot-new"
          className="inline-flex items-center gap-2 rounded-full bg-violet-700 hover:bg-violet-600 text-white px-5 py-2.5 text-sm font-semibold">
          <PlusCircle size={14} /> Yeni Çekim
        </button>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full border border-slate-200 hover:bg-slate-50 inline-flex items-center justify-center" data-testid="cal-prev"><ChevronLeft size={14} /></button>
          <div className="text-sm font-semibold text-slate-900 min-w-[280px] text-center" data-testid="cal-label">{headerLabel}</div>
          <button onClick={() => nav(1)} className="w-9 h-9 rounded-full border border-slate-200 hover:bg-slate-50 inline-flex items-center justify-center" data-testid="cal-next"><ChevronRight size={14} /></button>
          <button onClick={() => setRefDate(new Date())} className="ml-2 rounded-full border border-slate-200 hover:bg-slate-50 px-3 py-1.5 text-xs" data-testid="cal-today">Bugün</button>
        </div>
        <div className="flex items-center gap-2">
          {[["week","Hafta"],["month","Ay"],["list","Liste"]].map(([v,l]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium ${view === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
          ))}
          <div className="w-px h-6 bg-slate-200 mx-1" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="cal-status-filter"
            className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs">
            <option value="">Tüm Durumlar</option>
            <option value="onay_bekleniyor">Onay Bekleniyor</option>
            <option value="onaylandi">Onaylandı</option>
            <option value="iptal">İptal</option>
          </select>
        </div>
      </div>

      {view === "week" && (
        <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden" data-testid="week-view">
          <div className="grid" style={{ gridTemplateColumns: "70px repeat(7, 1fr)" }}>
            <div className="border-b border-slate-100 py-2 px-2 text-[10px] font-semibold text-slate-400 uppercase" />
            {weekDays.map((d, i) => {
              const isToday = iso(d) === iso(new Date());
              return (
                <div key={i} className="border-b border-l border-slate-100 py-3 px-3 text-center">
                  <div className="text-[10px] font-semibold uppercase text-slate-400 tracking-wider">{DAYS_TR[i]}</div>
                  <div className={`text-lg font-semibold mt-0.5 ${isToday ? "text-violet-700" : "text-slate-800"}`}>{d.getDate()}</div>
                </div>
              );
            })}
            {HOURS.map((h) => (
              <Fragment key={`hr-${h}`}>
                <div className="border-b border-slate-50 h-16 px-2 text-[11px] text-slate-400 text-right pr-2 pt-1">{String(h).padStart(2, "0")}:00</div>
                {weekDays.map((d, di) => {
                  const dateShoots = (shootsByDate[iso(d)] || []).filter((s) => {
                    const sh = parseInt((s.start_time || "00:00").split(":")[0]);
                    return sh === h;
                  });
                  return (
                    <div key={`c-${h}-${di}`} onDoubleClick={() => openNew(d)} className="border-b border-l border-slate-50 h-16 px-1 py-1 hover:bg-slate-50/50 relative">
                      {dateShoots.map((s) => {
                        const col = STATUS_COLORS[s.status] || STATUS_COLORS.onay_bekleniyor;
                        return (
                          <button key={s.id} onClick={() => openEdit(s)} data-testid={`shoot-block-${s.id}`}
                            className={`w-full text-left rounded-md px-2 py-1 mb-1 border ${col.bg} ${col.border} ${col.text} hover:opacity-90`}>
                            <div className={`text-[11px] font-semibold leading-tight truncate ${col.lineThrough ? "line-through" : ""}`}>{s.start_time} · {s.brand_name}</div>
                            <div className="text-[10px] opacity-75 truncate">{s.photographer_name}</div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </Fragment>
            ))}
          </div>
        </div>
      )}

      {view === "month" && (
        <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden" data-testid="month-view">
          <div className="grid grid-cols-7 border-b border-slate-100">
            {DAYS_TR.map((d) => <div key={d} className="py-3 text-center text-[10px] font-semibold uppercase text-slate-400 tracking-wider">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {(() => {
              const first = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
              const monthStart = startOfWeek(first);
              const cells = [];
              for (let i = 0; i < 42; i++) {
                const d = addDays(monthStart, i);
                const inMonth = d.getMonth() === refDate.getMonth();
                const isToday = iso(d) === iso(new Date());
                const dsh = shootsByDate[iso(d)] || [];
                cells.push(
                  <div key={i} onDoubleClick={() => openNew(d)}
                    className={`min-h-[100px] border-b border-l border-slate-50 p-1.5 ${inMonth ? "bg-white" : "bg-slate-50/40"} hover:bg-slate-50`}>
                    <div className={`text-xs font-semibold mb-1 ${isToday ? "text-violet-700" : inMonth ? "text-slate-700" : "text-slate-400"}`}>{d.getDate()}</div>
                    <div className="space-y-1">
                      {dsh.slice(0, 3).map((s) => {
                        const col = STATUS_COLORS[s.status] || STATUS_COLORS.onay_bekleniyor;
                        return (
                          <button key={s.id} onClick={() => openEdit(s)} data-testid={`shoot-block-${s.id}`}
                            className={`w-full text-left rounded px-1.5 py-0.5 border ${col.bg} ${col.border} ${col.text} text-[10px] font-medium truncate ${col.lineThrough ? "line-through" : ""}`}>
                            {s.start_time} {s.brand_name}
                          </button>
                        );
                      })}
                      {dsh.length > 3 && <div className="text-[10px] text-slate-400 px-1">+{dsh.length - 3} daha</div>}
                    </div>
                  </div>
                );
              }
              return cells;
            })()}
          </div>
        </div>
      )}

      {view === "list" && (
        <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden" data-testid="list-view">
          <table className="w-full">
            <thead><tr className="border-b border-slate-100 bg-slate-50/40">
              {["Tarih","Saat","Firma","Çekimci","Lokasyon","Durum",""].map((h) => <th key={h} className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? <tr><td colSpan={7} className="py-14 text-center text-sm text-slate-500">Bu aralıkta çekim yok.</td></tr>
                : filtered.map((s) => {
                  const col = STATUS_COLORS[s.status] || STATUS_COLORS.onay_bekleniyor;
                  return (
                    <tr key={s.id} className="border-b border-slate-50 last:border-none hover:bg-slate-50/40" data-testid={`list-row-${s.id}`}>
                      <td className="py-3 px-4 text-sm text-slate-700">{s.date}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{s.start_time}–{s.end_time}</td>
                      <td className={`py-3 px-4 text-sm font-medium text-slate-900 ${col.lineThrough ? "line-through" : ""}`}>{s.brand_name}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{s.photographer_name}{s.photographer_is_staff ? "" : ` · ${s.external_fee > 0 ? s.external_fee + " ₺" : ""}`}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{s.location || "—"}</td>
                      <td className="py-3 px-4"><span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border ${col.bg} ${col.border} ${col.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${col.dot}`} />{col.label}
                      </span></td>
                      <td className="py-3 px-4"><button onClick={() => openEdit(s)} data-testid={`list-edit-${s.id}`} className="text-xs text-violet-700 hover:underline">Düzenle</button></td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <ShootModal workspace={workspace} initialDate={modalDate} initialShoot={modalShoot} photographers={photographers}
          onClose={() => { setModalOpen(false); setModalShoot(null); }} onSaved={onSaved} />
      )}
    </FinanceLayout>
  );
}
